import { Box, Button, CircularProgress, Grid } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDialogBetter } from "../../../../../hooks/useDialogBetter";
import { AGREEMENT_PAYMENT_KINDS } from "../../../../../schemas/agreements";
import {
    isFinalSettlementPayment,
    isReservationPayment,
    useDefaultPaymentSchedule,
    usePaymentSchedule,
} from "../../../../../services/agreementsService";
import { useProductsByIds } from "../../../../../services/productsService";
import { PaymentsList } from "./PaymentsList";
import { PaymentsSummary } from "./PaymentsSummary";
import { ScheduleTemplateSelectionDialog } from "./ScheduleTemplateSelectionDialog";

// TODO better UX in number inputs
export const AgreementPaymentScheduleTab = (props) => {
    const { payments, setPayments, productIds, productDiscounts, readonly = false } = props;
    const { t } = useTranslation();
    const { openDialog } = useDialogBetter();

    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [shouldUpdatePaymentsFromTemplate, setShouldUpdatePaymentsFromTemplate] = useState(false);
    const hasAppliedDefaultSchedule = useRef(false);

    const { data: scheduleTemplate } = usePaymentSchedule(selectedTemplateId);
    const { data: products } = useProductsByIds(productIds);

    const investmentId = products?.[0].investment.id_invest_home;

    const { data: defaultSchedule } = useDefaultPaymentSchedule(investmentId);

    const isLoading = !products || (selectedTemplateId != null && !scheduleTemplate);

    const isReservationPaymentAlreadyPresent = useMemo(
        () => payments.some(isReservationPayment),
        [payments],
    );

    const createPaymentsFromSchedule = useCallback(
        (schedule) => {
            const installments = schedule.installments.sort(
                (a, b) => a.installment_order - b.installment_order,
            );

            return installments.map((installment) => ({
                agrepay_kind: AGREEMENT_PAYMENT_KINDS.Tranche,
                agrepay_milestone: null,
                agrepay_due_date: null,
                agrepay_payment_shift: 0,
                per_product_percentages: productIds.map((pid) => ({
                    product_id: pid,
                    percentage: Number(installment.installment_percentage),
                })),
            }));
        },
        [productIds],
    );

    const handleOpenCreateFromTemplateDialog = () => {
        openDialog({
            Component: ScheduleTemplateSelectionDialog,
            componentProps: {
                selectedTemplateId,
                onTemplateSelect: (newId) => {
                    setSelectedTemplateId(newId);
                    setShouldUpdatePaymentsFromTemplate(true);
                },
            },
        });
    };

    const handleAddEmptyPayment = useCallback(
        (kindId) => {
            if (isLoading) {
                return;
            }

            const newPayment = {
                agrepay_kind: kindId,
                agrepay_milestone: null,
                agrepay_due_date: null,
                agrepay_payment_shift: 0,
                per_product_percentages: [
                    ...products.map((product) => ({
                        product_id: product.id,
                        percentage: 0,
                    })),
                ],
            };

            const finalSettlementPayment = payments.filter(p=>isFinalSettlementPayment(p))

            setPayments(
                kindId === AGREEMENT_PAYMENT_KINDS.Reservation
                    ? [newPayment, ...payments.filter(p=>!isFinalSettlementPayment(p)), ...finalSettlementPayment]
                    : [...payments.filter(p=>!isFinalSettlementPayment(p)), newPayment, ...finalSettlementPayment],
            );
        },
        [isLoading, payments, products, setPayments],
    );

    // Apply default schedule when entering the tab with no existing payments
    useEffect(() => {
        if (
            defaultSchedule == null ||
            hasAppliedDefaultSchedule.current ||
            payments.length > 0 ||
            productIds.length === 0
        ) {
            return;
        }

        const newPayments = createPaymentsFromSchedule(defaultSchedule);
        setPayments(newPayments);
        hasAppliedDefaultSchedule.current = true;
    }, [defaultSchedule, payments.length, productIds, setPayments, createPaymentsFromSchedule]);

    // Update payments when a schedule template is selected
    useEffect(() => {
        if (scheduleTemplate == null || !shouldUpdatePaymentsFromTemplate) {
            return;
        }

        const newPayments = createPaymentsFromSchedule(scheduleTemplate);
        setPayments(newPayments);
        setShouldUpdatePaymentsFromTemplate(false);
    }, [
        scheduleTemplate,
        setPayments,
        shouldUpdatePaymentsFromTemplate,
        createPaymentsFromSchedule,
    ]);

    if (isLoading) {
        return <CircularProgress />;
    }

    return (
        <Grid container spacing={2}>
            <Grid item xs={3}>
                <Button onClick={handleOpenCreateFromTemplateDialog} disabled={readonly}>
                    {t("agreements.AgreementPaymentScheduleTab.createScheduleFromTemplate")}
                </Button>
            </Grid>
            <Grid item xs={3}>
                <Button
                    onClick={() => handleAddEmptyPayment(AGREEMENT_PAYMENT_KINDS.Tranche)}
                    disabled={readonly}
                >
                    {t("agreements.AgreementPaymentScheduleTab.addPaymentTranche")}
                </Button>
            </Grid>
            <Grid item xs={3}>
                <Button
                    onClick={() => handleAddEmptyPayment(AGREEMENT_PAYMENT_KINDS.Reservation)}
                    disabled={readonly || isReservationPaymentAlreadyPresent}
                >
                    {t("agreements.AgreementPaymentScheduleTab.addReservationPayment")}
                </Button>
            </Grid>
            <Grid item xs={8}>
                <Box sx={{ height: "50vh", overflowY: "auto", p: 2 }}>
                    <PaymentsList
                        payments={payments}
                        setPayments={setPayments}
                        includesReservationPayment={isReservationPaymentAlreadyPresent}
                        investmentId={investmentId}
                        products={products}
                        productDiscounts={productDiscounts}
                        readonly={readonly}
                    />
                </Box>
            </Grid>
            <Grid item xs={4}>
                <PaymentsSummary
                    payments={payments}
                    products={products}
                    productDiscounts={productDiscounts}
                />
            </Grid>
        </Grid>
    );
};
